import {
  AlertDialog,
  Button,
  Chip,
  ListBox,
  Pagination,
  Separator,
  toast,
  cn,
} from '@heroui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Selection } from 'react-aria-components';
import type { PaginationState } from '@hs/ui';
import { DataGrid, InlineSelect, Sheet } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { IconTrash as Trash2, IconPlus as Plus } from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { SheetOverlayPortalHost } from '@/components/SheetOverlayPortalProvider';
import { AddSpaceAppsModal } from './AddSpaceAppsModal';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

export interface SpaceAppsSheetProps {
  open: boolean;
  spaceId: string;
  spaceName: string;
  onCancel: () => void;
}

type SpaceAppRow = {
  id: string;
  orderId?: string | null;
  isEnabled?: boolean | null;
  isPublic?: boolean | null;
  admin?: Array<{
    id?: string | null;
    username?: string | null;
    nickname?: string | null;
    avatarUrl?: string | null;
  }> | null;
  createdAt?: string | Date | null;
  updatedAt?: string | Date | null;
  app?: { id?: string | null; name?: string | null } | null;
};

const DEFAULT_PAGE_SIZE = 10;

function buildPaginationPages(totalPages: number, safePage: number): (number | 'ellipsis')[] {
  const pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safePage > 3) pages.push('ellipsis');
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
  }
  return pages;
}

export function SpaceAppsSheet({ open, spaceId, spaceName, onCancel }: SpaceAppsSheetProps) {
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set());
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [overlayPortalHost, setOverlayPortalHost] = useState<HTMLElement | null>(null);

  const { data, isLoading, refetch } = trpc.spaceApps.listAll.useQuery({ spaceId }, { enabled: open });

  const utils = trpc.useUtils();

  const [listReady, setListReady] = useState(false);
  useEffect(() => {
    if (!open) {
      setListReady(false);
      return;
    }
    let cancelled = false;
    refetch().then(() => {
      if (!cancelled) setListReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [open, spaceId, refetch]);

  useEffect(() => {
    if (!open) {
      setSelectedKeys(new Set());
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }
  }, [open]);

  const deleteManyMutation = trpc.spaceApps.deleteMany.useMutation({
    onSuccess: (_data, variables) => {
      toast.success(`已删除 ${variables.ids.length} 个应用`);
      setSelectedKeys(new Set());
      setDeleteDialogOpen(false);
      utils.spaceApps.listAll.invalidate({ spaceId });
      refetch();
    },
    onError: (error) => {
      toast.danger(error.message || '删除失败');
    },
  });

  const rows = useMemo(() => (data ?? []) as SpaceAppRow[], [data]);

  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / (pagination.pageSize || DEFAULT_PAGE_SIZE)));

  const pageRows = useMemo(() => {
    const size = pagination.pageSize || DEFAULT_PAGE_SIZE;
    const start = pagination.pageIndex * size;
    return rows.slice(start, start + size);
  }, [rows, pagination]);

  const formatDate = useCallback((value: string | Date | null | undefined) => {
    if (!value) return '-';
    const d = typeof value === 'string' ? new Date(value) : value;
    return d.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }, []);

  const selectedIds = useMemo(() => {
    if (selectedKeys === 'all') return rows.map((r) => r.id).filter(Boolean);
    return [...(selectedKeys as Set<string>)];
  }, [selectedKeys, rows]);

  const columns = useMemo<DataGridColumn<SpaceAppRow>[]>(
    () => [
      {
        id: 'appName',
        accessorKey: 'app',
        header: '应用名称',
        allowsSorting: false,
        allowsResizing: true,
        minWidth: 160,
        cell: (row) => row.app?.name ?? '-',
      },
      {
        id: 'orderId',
        accessorKey: 'orderId',
        header: '订单ID',
        allowsSorting: false,
        allowsResizing: true,
        minWidth: 120,
        cell: (row) => row.orderId || '-',
      },
      {
        id: 'isEnabled',
        accessorKey: 'isEnabled',
        header: '是否启用',
        allowsSorting: false,
        allowsResizing: true,
        minWidth: 100,
        cell: (row) =>
          row.isEnabled ? (
            <Chip color="success" size="sm" variant="soft">
              <Chip.Label>是</Chip.Label>
            </Chip>
          ) : (
            <Chip color="default" size="sm" variant="soft">
              <Chip.Label>否</Chip.Label>
            </Chip>
          ),
      },
      {
        id: 'isPublic',
        accessorKey: 'isPublic',
        header: '是否公开',
        allowsSorting: false,
        allowsResizing: true,
        minWidth: 100,
        cell: (row) =>
          row.isPublic ? (
            <Chip color="success" size="sm" variant="soft">
              <Chip.Label>是</Chip.Label>
            </Chip>
          ) : (
            <Chip color="default" size="sm" variant="soft">
              <Chip.Label>否</Chip.Label>
            </Chip>
          ),
      },
      {
        id: 'admin',
        accessorKey: 'admin',
        header: '管理员',
        allowsSorting: false,
        allowsResizing: true,
        minWidth: 88,
        cell: (row) => {
          const count = Array.isArray(row.admin) ? row.admin.length : 0;
          return count > 0 ? `${count} 人` : '—';
        },
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: '创建时间',
        allowsSorting: false,
        allowsResizing: true,
        minWidth: 170,
        cell: (row) => (
          <span className="whitespace-nowrap">{formatDate(row.createdAt ?? undefined)}</span>
        ),
      },
      {
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        header: '更新时间',
        allowsSorting: false,
        allowsResizing: true,
        minWidth: 170,
        cell: (row) => (
          <span className="whitespace-nowrap">{formatDate(row.updatedAt ?? undefined)}</span>
        ),
      },
    ],
    [formatDate]
  );

  const columnsWithRowHeader = useMemo(() => {
    const first = columns[0];
    const fh = first?.id;
    return columns.map((c) => ({
      ...c,
      isRowHeader: c.id === fh,
    }));
  }, [columns]);

  const handleSheetOpenChange = useCallback(
    (next: boolean) => {
      if (!next && addModalOpen) return;
      if (!next) onCancel();
    },
    [onCancel, addModalOpen]
  );

  const handleConfirmDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warning('请先选择要删除的应用');
      return;
    }
    await deleteManyMutation.mutateAsync({ ids: selectedIds });
  };

  const existingAppIds = useMemo(
    () => rows.map((row) => row.app?.id).filter((id): id is string => !!id),
    [rows]
  );

  const safePage = Math.min(pagination.pageIndex + 1, pageCount);
  const paginationPages = useMemo(
    () => buildPaginationPages(pageCount, safePage),
    [pageCount, safePage]
  );

  const pageSizes = [10, 20, 50, 100];

  return (
    <>
      <Sheet isDismissable={!addModalOpen} isOpen={open} placement="right" onOpenChange={handleSheetOpenChange}>
        <Sheet.Backdrop>
          <Sheet.Content className="w-[min(1200px,85vw)]">
            <SheetOverlayPortalHost onHostChange={setOverlayPortalHost}>
              <Sheet.Dialog className="flex h-[100dvh] flex-col">
              <Sheet.Header className="gap-1 shrink-0">
                <Sheet.Heading className="text-xl font-medium">空间应用管理 - {spaceName}</Sheet.Heading>
              </Sheet.Header>
              <Sheet.Body className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-4">
                {!listReady ? (
                  <div className="text-muted-foreground flex flex-1 items-center justify-center text-sm">
                    加载中…
                  </div>
                ) : (
                  <>
                    <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button onPress={() => setAddModalOpen(true)}>
                          <Plus className="size-4" stroke={1.5} />
                          添加
                        </Button>
                        <Button
                          variant="danger"
                          isDisabled={selectedIds.length === 0}
                          onPress={() => {
                            if (selectedIds.length === 0) {
                              toast.warning('请先选择要删除的应用');
                              return;
                            }
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="size-4" stroke={1.5} />
                          删除
                        </Button>
                      </div>
                    </div>

                    <div className="relative grid min-h-0 min-w-0 flex-1 grid-rows-[minmax(0,1fr)_auto] gap-3 overflow-hidden">
                      <div className="relative min-h-0 min-w-0 overflow-hidden">
                        <div className="absolute inset-0 flex min-h-0 min-w-0 flex-col">
                          {isLoading ? (
                            <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center text-sm">
                              加载中…
                            </div>
                          ) : null}
                          <DataGrid<SpaceAppRow>
                            allowsColumnResize
                            aria-label="空间应用列表"
                            className="flex h-full min-h-0 min-w-0 flex-col"
                            columns={columnsWithRowHeader}
                            contentClassName="min-w-0"
                            data={pageRows}
                            getRowId={(item) => item.id}
                            renderEmptyState={() => (
                              <div className="text-muted-foreground py-8 text-center text-sm">
                                当前空间暂无应用
                              </div>
                            )}
                            scrollContainerClassName={cn(
                              'h-full max-h-full min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain',
                              '[&_[data-slot=table-resizable-container]]:overflow-visible',
                              '[&_[data-slot=table-content]]:!overflow-visible',
                              '[&_thead_th]:bg-[var(--surface-secondary)] [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20',
                              '[&_th]:min-w-0 [&_td]:min-w-0',
                              '[&_[role=columnheader]]:min-w-0 [&_[role=gridcell]]:min-w-0'
                            )}
                            selectedKeys={selectedKeys}
                            selectionMode="multiple"
                            showSelectionCheckboxes
                            variant="primary"
                            onSelectionChange={setSelectedKeys}
                          />
                        </div>
                      </div>

                      <div
                        className="text-muted-foreground flex min-w-0 shrink-0 flex-nowrap items-center justify-between gap-3 overflow-x-auto text-sm"
                        data-slot="space-apps-pagination"
                      >
                        <div className="flex w-fit shrink-0 items-center">
                          <Pagination className="w-fit max-w-none" size="sm">
                            <Pagination.Content className="flex w-fit min-w-0">
                              <Pagination.Item>
                                <Pagination.Previous
                                  isDisabled={safePage <= 1}
                                  onPress={() =>
                                    setPagination((p) => ({
                                      ...p,
                                      pageIndex: Math.max(0, p.pageIndex - 1),
                                    }))
                                  }
                                >
                                  <Pagination.PreviousIcon />
                                </Pagination.Previous>
                              </Pagination.Item>
                              {paginationPages.map((p, i) =>
                                p === 'ellipsis' ? (
                                  <Pagination.Item key={`e-${i}`}>
                                    <Pagination.Ellipsis />
                                  </Pagination.Item>
                                ) : (
                                  <Pagination.Item key={p}>
                                    <Pagination.Link
                                      isActive={p === safePage}
                                      onPress={() =>
                                        setPagination((prev) => ({
                                          ...prev,
                                          pageIndex: (p as number) - 1,
                                        }))
                                      }
                                    >
                                      {p}
                                    </Pagination.Link>
                                  </Pagination.Item>
                                )
                              )}
                              <Pagination.Item>
                                <Pagination.Next
                                  isDisabled={safePage >= pageCount}
                                  onPress={() =>
                                    setPagination((p) => ({
                                      ...p,
                                      pageIndex: Math.min(pageCount - 1, p.pageIndex + 1),
                                    }))
                                  }
                                >
                                  <Pagination.NextIcon />
                                </Pagination.Next>
                              </Pagination.Item>
                            </Pagination.Content>
                          </Pagination>
                        </div>

                        <div className="flex h-4 w-fit shrink-0 flex-nowrap items-center gap-3 whitespace-nowrap">
                          <InlineSelect
                            aria-label="每页条数"
                            value={String(pagination.pageSize)}
                            onChange={(v) => {
                              if (v) {
                                setPagination((p) => ({
                                  ...p,
                                  pageSize: Number(v),
                                  pageIndex: 0,
                                }));
                              }
                            }}
                          >
                            <InlineSelect.Trigger>
                              <span className="text-muted">每页</span>
                              <InlineSelect.Value />
                              <InlineSelect.Indicator />
                            </InlineSelect.Trigger>
                            <InlineSelect.Popover className="w-[88px]">
                              <ListBox>
                                {pageSizes.map((n) => (
                                  <ListBox.Item key={n} id={String(n)} textValue={String(n)}>
                                    {n}
                                    <ListBox.ItemIndicator />
                                  </ListBox.Item>
                                ))}
                              </ListBox>
                            </InlineSelect.Popover>
                          </InlineSelect>
                          <Separator className="!h-4 shrink-0" orientation="vertical" />
                          <span className="text-muted shrink-0">
                            第 {total === 0 ? 0 : (safePage - 1) * pagination.pageSize + 1}–
                            {Math.min(safePage * pagination.pageSize, total)} 条，共 {total} 条
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </Sheet.Body>
            </Sheet.Dialog>

            <AddSpaceAppsModal
              existingAppIds={existingAppIds}
              open={addModalOpen}
              portalContainer={overlayPortalHost}
              spaceId={spaceId}
              onCancel={() => setAddModalOpen(false)}
              onSuccess={() => {
                setAddModalOpen(false);
                utils.spaceApps.listAll.invalidate({ spaceId });
                refetch();
              }}
            />
          </SheetOverlayPortalHost>
          </Sheet.Content>
        </Sheet.Backdrop>
      </Sheet>

      <AlertDialog isOpen={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除选中的 {selectedIds.length} 个应用吗？此操作不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => setDeleteDialogOpen(false)}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteManyMutation.isPending}
                  onPress={() => void handleConfirmDelete()}
                >
                  {deleteManyMutation.isPending ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </>
  );
}
